import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { apiClient, getApiErrorMessage } from '../../lib/api-client';
import { useAuthStore } from '../../shared/auth/authStore';
import { findModule, type ModuleResource, type ResourceAction } from './module-catalog';

type DataRow = Record<string, unknown> & { id?: string; status?: string };

interface PageResult {
  items: DataRow[];
  total: number;
}

const PRIORITY_COLUMNS = [
  'docNo',
  'code',
  'name',
  'invoiceNo',
  'poNo',
  'prNo',
  'employeeCode',
  'title',
  'status',
  'postingDate',
  'invoiceDate',
  'dueDate',
  'total',
  'amount',
  'currency',
  'createdAt',
];

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  APPROVED: 'cyan',
  COMPLETED: 'green',
  DRAFT: 'default',
  INACTIVE: 'default',
  PAID: 'green',
  PARTIALLY_PAID: 'gold',
  PENDING: 'gold',
  PENDING_APPROVAL: 'blue',
  POSTED: 'purple',
  REJECTED: 'red',
  BLOCKED: 'red',
  CANCELLED: 'red',
};

function unwrapPage(data: unknown): PageResult {
  const payload =
    typeof data === 'object' && data !== null && 'data' in data
      ? (data as { data: unknown }).data
      : data;
  if (Array.isArray(payload)) return { items: payload as DataRow[], total: payload.length };
  if (typeof payload === 'object' && payload !== null && 'items' in payload) {
    const page = payload as { items?: unknown; total?: unknown };
    return {
      items: Array.isArray(page.items) ? (page.items as DataRow[]) : [],
      total: typeof page.total === 'number' ? page.total : 0,
    };
  }
  if (typeof payload === 'object' && payload !== null) return { items: [payload as DataRow], total: 1 };
  return { items: [], total: 0 };
}

function humanize(key: string): string {
  const labels: Record<string, string> = {
    id: 'ID',
    code: 'Mã',
    name: 'Tên',
    title: 'Tiêu đề',
    docNo: 'Số chứng từ',
    invoiceNo: 'Số hóa đơn',
    poNo: 'Số PO',
    prNo: 'Số PR',
    employeeCode: 'Mã NV',
    status: 'Trạng thái',
    postingDate: 'Ngày hạch toán',
    invoiceDate: 'Ngày hóa đơn',
    dueDate: 'Ngày đến hạn',
    amount: 'Số tiền',
    total: 'Tổng tiền',
    currency: 'Tiền tệ',
    createdAt: 'Ngày tạo',
    updatedAt: 'Ngày cập nhật',
  };
  return labels[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

function isDateKey(key: string): boolean {
  return /(?:Date|At)$/.test(key);
}

function isMoneyKey(key: string): boolean {
  return /amount|total|balance|cost|price/i.test(key);
}

function renderValue(value: unknown, key: string): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'status' && typeof value === 'string') {
    return <Tag color={STATUS_COLORS[value] ?? 'blue'}>{value}</Tag>;
  }
  if (typeof value === 'boolean') return value ? <Tag color="green">Có</Tag> : <Tag>Không</Tag>;
  if (isDateKey(key) && (typeof value === 'string' || value instanceof Date)) {
    const date = dayjs(value);
    return date.isValid() ? date.format(key.endsWith('At') ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : String(value);
  }
  if (isMoneyKey(key) && (typeof value === 'string' || typeof value === 'number')) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric.toLocaleString('vi-VN', { maximumFractionDigits: 4 });
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function ModuleWorkspacePage(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const module = findModule(code);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get('view') ?? module?.resources[0]?.key;
  const resource = module?.resources.find((item) => item.key === selectedKey) ?? module?.resources[0];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DataRow | null>(null);
  const activeCompanyId = useAuthStore((state) => state.activeCompanyId);
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const queryKey = ['module-resource', code, resource?.key, activeCompanyId, page, pageSize, search];
  const query = useQuery({
    queryKey,
    enabled: resource !== undefined,
    queryFn: async () => {
      const { data } = await apiClient.get(resource!.endpoint, {
        params: { page, pageSize, ...(search ? { search } : {}) },
      });
      return unwrapPage(data);
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({ row, action }: { row: DataRow; action: ResourceAction }) => {
      if (!row.id || !resource) throw new Error('Bản ghi không có ID');
      await apiClient.post(`${resource.endpoint}/${row.id}/${action.key}`, {});
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['module-resource', code, resource?.key] });
      message.success('Đã thực hiện hành động');
    },
    onError: (error) => message.error(getApiErrorMessage(error)),
  });

  const columns = useMemo<ColumnsType<DataRow>>(() => {
    const sample = query.data?.items[0];
    if (!sample) return [];
    const keys = Object.keys(sample)
      .filter((key) => !Array.isArray(sample[key]) && typeof sample[key] !== 'object')
      .sort((a, b) => {
        const ai = PRIORITY_COLUMNS.indexOf(a);
        const bi = PRIORITY_COLUMNS.indexOf(b);
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      })
      .slice(0, 8);
    const dataColumns: ColumnsType<DataRow> = keys.map((key) => ({
      title: humanize(key),
      dataIndex: key,
      key,
      ellipsis: true,
      render: (value: unknown) => renderValue(value, key),
    }));
    dataColumns.push({
      title: '',
      key: 'actions',
      fixed: 'right',
      width: resource?.actions?.length ? 240 : 64,
      render: (_, row) => (
        <Space size="small">
          <Button type="text" icon={<EyeOutlined />} onClick={() => setSelected(row)}>
            Chi tiết
          </Button>
          {resource?.actions
            ?.filter((action) => !action.allowedStatuses || action.allowedStatuses.includes(row.status ?? ''))
            .map((action) => (
              <Button
                key={action.key}
                size="small"
                danger={action.danger === true}
                loading={actionMutation.isPending}
                onClick={() =>
                  modal.confirm({
                    title: action.label,
                    content: 'Bạn có chắc muốn thực hiện hành động này?',
                    okText: 'Xác nhận',
                    cancelText: 'Hủy',
                    onOk: () => actionMutation.mutateAsync({ row, action }),
                  })
                }
              >
                {action.label}
              </Button>
            ))}
        </Space>
      ),
    });
    return dataColumns;
  }, [actionMutation.isPending, modal, query.data?.items, resource]);

  if (!module || !resource) return <Empty description="Không tìm thấy phân hệ" />;

  const handleTableChange = (pagination: TablePaginationConfig): void => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 20);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>{module.label}</Typography.Title>
        <Typography.Text type="secondary">{module.description}</Typography.Text>
      </div>
      <Segmented
        block
        value={resource.key}
        options={module.resources.map((item) => ({ label: item.label, value: item.key }))}
        onChange={(value) => {
          setPage(1);
          setSearchParams({ view: String(value) });
        }}
      />
      <Card
        title={resource.label}
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder="Tìm kiếm"
              onSearch={(value) => {
                setPage(1);
                setSearch(value.trim());
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => query.refetch()}>Làm mới</Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary">{resource.description}</Typography.Paragraph>
        <Table<DataRow>
          rowKey={(row) => row.id ?? JSON.stringify(row)}
          loading={query.isLoading}
          columns={columns}
          dataSource={query.data?.items ?? []}
          scroll={{ x: 900 }}
          pagination={{ current: page, pageSize, total: query.data?.total ?? 0, showSizeChanger: true }}
          onChange={handleTableChange}
          locale={{
            emptyText: query.isError
              ? <Empty description={getApiErrorMessage(query.error)} />
              : <Empty description="Chưa có dữ liệu" />,
          }}
        />
      </Card>
      <Drawer title="Chi tiết bản ghi" width={720} open={selected !== null} onClose={() => setSelected(null)}>
        {selected && (
          <Descriptions bordered column={1} size="small">
            {Object.entries(selected).map(([key, value]) => (
              <Descriptions.Item key={key} label={humanize(key)}>
                {renderValue(value, key)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Drawer>
    </Space>
  );
}
